import React, { useState, useRef, useEffect } from "react";
import { IonButton } from "@ionic/react";
import "../profile/ProfileComponents.css";
import sanitizeHTML from "@components/wellbeing/wellbeingParts/helpers/sanitizeHTML";
import classNames from "classnames";

type Props = {
  title: string;
  subTitle?: string;
  children?: React.ReactNode;
  subTitleIsError?: boolean;
  style?: React.CSSProperties;
  sideButton?: boolean;
  sideButtonText?: string;
  sidePill?: boolean;
  sidePillColor?: "red" | "amber" | "green";
  sidePillText?: string;
  topPill?: boolean;
  topPillText?: string;
  buttonClicked?: () => void;
  isHTMLSubtitle?: boolean;
  className?: string;
  collapsible?: boolean;
  updateCollapsibleState?: (collapsed: boolean) => void;
  fullWidthChildren?: boolean;
};

const MAX_SUBTITLE_HEIGHT_BOTTOM_PADDING = 4;
const THREE_LINE_HEIGHT_THRESHOLD = 4.5; // Approximately 3 lines in em units

const ProgramInfoCard: React.FC<Props> = (props) => {
  const [isSubtitleExpanded, setIsSubtitleExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [shouldShowCollapsible, setShouldShowCollapsible] = useState<boolean>(true);

  // Measure content height on mount to determine if collapsible should be shown
  useEffect(() => {
    if (props.collapsible && props.subTitle && measurementRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (measurementRef.current) {
          const measuredHeight = measurementRef.current.scrollHeight;
          // Convert 4.5em threshold to pixels (approximate: 1em ≈ 16px for 13px font)
          // Using getComputedStyle to get actual line-height
          const computedStyle = window.getComputedStyle(measurementRef.current);
          const fontSize = parseFloat(computedStyle.fontSize) || 13;
          const lineHeight = parseFloat(computedStyle.lineHeight) || fontSize * 1.5;
          const thresholdPixels = THREE_LINE_HEIGHT_THRESHOLD * lineHeight;

          // If content height is <= 3 lines, hide collapsible
          setShouldShowCollapsible(measuredHeight > thresholdPixels);
        }
      });
    }
  }, [props.subTitle, props.collapsible]);

  // Measure content height when expanded or when content changes
  useEffect(() => {
    if (isSubtitleExpanded && contentRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (contentRef.current) {
          // Measure the full height when expanded (line-clamp-3 is removed)
          setContentHeight(contentRef.current.scrollHeight + MAX_SUBTITLE_HEIGHT_BOTTOM_PADDING);
        }
      });
    }
    // Don't reset height when collapsed - keep it for smooth collapse animation
    // Only re-measure when subtitle content changes
  }, [isSubtitleExpanded, props.subTitle]);

  if (props.collapsible) {
    return (
      <div className={classNames("flex justify-center", props.className)}>
        <div
          className="accountCardInner"
          style={props.style}
        >
          <div className="dashboardHeader">
            <div className="accountCardTitle">{props.title}</div>
            {props.sideButton && (
              <IonButton
                mode="ios"
                className="dashboardSideButton"
                onClick={() => props.buttonClicked?.()}
              >
                {props.sideButtonText || "Edit"}
              </IonButton>
            )}
            {props.sidePill && (
              <div
                className={classNames(
                  "h-5 flex shrink-0 items-center px-[8px] py-[2.25] justify-center h-20px rounded-[10px] bg-cta-red",
                  {
                    "!bg-cta-red": props.sidePillColor === "red",
                    "!bg-[#FEEBC8]": props.sidePillColor === "amber",
                    "!bg-[#C6F7C1]": props.sidePillColor === "green",
                  }
                )}
              >
                <div
                  className={classNames("h-5 leading-[20px] text-[13px] my-[2.25] font-semibold text-white", {
                    "!text-white": props.sidePillColor === "red",
                    "!text-[#9C4221]": props.sidePillColor === "amber",
                    "!text-[#085333]": props.sidePillColor === "green",
                  })}
                >
                  {props.sidePillText}
                </div>
              </div>
            )}
          </div>
          {props.subTitle && (
            <>
              {/* Hidden measurement element to determine if content exceeds 3 lines */}
              <div
                ref={measurementRef}
                className="absolute opacity-0 pointer-events-none invisible"
                style={{ position: "absolute", top: "-9999px" }}
              >
                {props.isHTMLSubtitle ? (
                  <div
                    className="accountCardSubTitle text-acc-13px [&>p]!:text-acc-13px [&>p]:!font-acc-normal [&>p]:!leading-default [&>p]:!tracking-default [&>p]:!text-grey-70 program-info-card-description whitespace-pre-wrap"
                    dangerouslySetInnerHTML={sanitizeHTML(props.subTitle)}
                  />
                ) : (
                  <div className="accountCardSubTitle">{props.subTitle}</div>
                )}
              </div>

              {shouldShowCollapsible ? (
                <div>
                  <div
                    className="overflow-hidden transition-all duration-[250ms] ease-in-out"
                    style={{
                      maxHeight: isSubtitleExpanded
                        ? contentHeight
                          ? `${contentHeight}px`
                          : "1000px" // Fallback while measuring
                        : "4.5em", // Collapsed state
                    }}
                  >
                    {props.isHTMLSubtitle ? (
                      <div
                        ref={contentRef}
                        id={
                          isSubtitleExpanded ? "progress-check-list-subtitle-expanded" : "progress-check-list-subtitle"
                        }
                        className={classNames(
                          "accountCardSubTitle text-acc-13px [&>p]!:text-acc-13px [&>p]:!font-acc-normal [&>p]:!leading-default [&>p]:!tracking-default [&>p]:!text-grey-70 program-info-card-description whitespace-pre-wrap",
                          {
                            "line-clamp-3": !isSubtitleExpanded,
                          }
                        )}
                        dangerouslySetInnerHTML={sanitizeHTML(props.subTitle)}
                      />
                    ) : (
                      <div
                        ref={contentRef}
                        className={classNames("accountCardSubTitle", {
                          "line-clamp-3": !isSubtitleExpanded,
                        })}
                      >
                        {props.subTitle}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-start mt-2">
                    <button
                      type="button"
                      className="text-[13px] font-semibold tracking-[-0.05px] text-cta-blue acc-underline mt-1 cursor-pointer self-end bg-transparent border-none p-0"
                      onClick={() => {
                        setIsSubtitleExpanded(!isSubtitleExpanded);
                        props.updateCollapsibleState?.(!isSubtitleExpanded);
                      }}
                    >
                      {isSubtitleExpanded ? "Show less" : "Show more"}
                    </button>
                  </div>
                </div>
              ) : (
                // Render normally without collapsible functionality for short text
                <>
                  {props.isHTMLSubtitle ? (
                    <div
                      id="progress-check-list-subtitle"
                      className="accountCardSubTitle text-acc-13px [&>p]!:text-acc-13px [&>p]:!font-acc-normal [&>p]:!leading-default [&>p]:!tracking-default [&>p]:!text-grey-70 program-info-card-description whitespace-pre-wrap"
                      dangerouslySetInnerHTML={sanitizeHTML(props.subTitle)}
                    />
                  ) : (
                    <div className="accountCardSubTitle">{props.subTitle}</div>
                  )}
                </>
              )}
            </>
          )}
          <div
            className="programCardContent"
            style={props.fullWidthChildren ? { marginLeft: "calc(-16px - var(--ion-safe-area-left))", marginRight: "calc(-16px - var(--ion-safe-area-right))" } : undefined}
          >
            {props.children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames("flex justify-center", props.className)}>
      <div
        className="accountCardInner"
        style={props.style}
      >
        <div className="dashboardHeader">
          <div className="accountCardTitle">{props.title}</div>
          {props.sideButton && (
            <IonButton
              mode="ios"
              className="dashboardSideButton"
              onClick={() => props.buttonClicked?.()}
            >
              {props.sideButtonText || "Edit"}
            </IonButton>
          )}
          {props.sidePill && (
            <div
              className={classNames(
                "h-5 flex shrink-0 items-center px-[8px] py-[2.25] justify-center h-20px rounded-[10px] bg-cta-red",
                {
                  "!bg-cta-red": props.sidePillColor === "red",
                  "!bg-[#FEEBC8]": props.sidePillColor === "amber",
                  "!bg-[#C6F7C1]": props.sidePillColor === "green",
                }
              )}
            >
              <div
                className={classNames("h-5 leading-[20px] text-[13px] my-[2.25] font-semibold text-white", {
                  "!text-white": props.sidePillColor === "red",
                  "!text-[#9C4221]": props.sidePillColor === "amber",
                  "!text-[#085333]": props.sidePillColor === "green",
                })}
              >
                {props.sidePillText}
              </div>
            </div>
          )}
        </div>
        {props.subTitle && (
          <>
            {props.subTitleIsError ? (
              <div className="p-[12px] rounded-[4px] bg-red-background mt-[5.5px] mb-[-12.5px]">
                <div className="font-text-acc-16px font-acc-normal leading-acc-lh tracking-default text-red-text">
                  {props.subTitle}
                </div>
              </div>
            ) : (
              <>
                {props.isHTMLSubtitle ? (
                  <div
                    id="progress-check-list-subtitle"
                    className="accountCardSubTitle text-acc-13px [&>p]!:text-acc-13px [&>p]:!font-acc-normal [&>p]:!leading-default [&>p]:!tracking-default [&>p]:!text-grey-70 program-info-card-description whitespace-pre-wrap"
                    dangerouslySetInnerHTML={sanitizeHTML(props.subTitle)}
                  />
                ) : (
                  <div className="accountCardSubTitle">{props.subTitle}</div>
                )}
              </>
            )}
          </>
        )}
        <div
          className="programCardContent"
          style={props.fullWidthChildren ? { marginLeft: "calc(-16px - var(--ion-safe-area-left))", marginRight: "calc(-16px - var(--ion-safe-area-right))" } : undefined}
        >
          {props.children}
        </div>
      </div>
    </div>
  );
};

export default ProgramInfoCard;
